import { Link, Page, Text, View } from '@react-pdf/renderer'
import { Result } from 'antd'
import { PageHeader } from 'components/PageHeader'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { TOCSection, TOCSectionProps } from 'features/reporting/types'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const TOCContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
`

const RenderReact: React.FC = () => {
  return (
    <TOCContainer>
      <Result
        status='info'
        title='Table of Contents'
        subTitle='The Table of Contents is automatically generated in the PDF output. Navigate to "Generate PDF" to preview and download the report with the complete table of contents.'
      />
    </TOCContainer>
  )
}

const RenderPDF: React.FC<TOCSectionProps> = ({
  entries,
  sectionPages,
  onRender,
}) => {
  const { formData, hasPhasedArrayInspection, weldIndicationsWithImages } =
    useRautReportOps()

  const tocEntries = useMemo(() => {
    const hasWeldScanData = (formData.weldScanData?.scans?.length ?? 0) > 0
    const hasVisualizations = weldIndicationsWithImages.length > 0
    const showWeldSection =
      hasPhasedArrayInspection && (hasWeldScanData || hasVisualizations)

    const filtered = showWeldSection
      ? entries
      : entries.filter((entry) => entry.id !== 'weldScanIndications')

    const result: Array<{ id: string; title: string }> = []
    for (const section of filtered) {
      if (
        section.title === 'Indications' &&
        formData.mainInfo.scanIndicationMode
      ) {
        result.push({ id: section.id, title: 'Scan Summary Table' })
        result.push({ id: 'scan-details', title: 'Scan Details' })
      } else {
        result.push(section)
      }
    }
    return result
  }, [
    entries,
    formData.mainInfo.scanIndicationMode,
    formData.weldScanData?.scans?.length,
    hasPhasedArrayInspection,
    weldIndicationsWithImages.length,
  ])

  return (
    <Page size='A4' style={PDFStyles.page} id='toc'>
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'tableOfContents', pageNumber })
          return ''
        }}
      />

      <PageHeader title='Table of Contents' />

      <View style={PDFStyles.tocTableHeader}>
        <Text style={PDFStyles.tocSectionColumn}>Section</Text>
        <Text style={PDFStyles.tocTitleColumn}>Title</Text>
        <Text style={PDFStyles.tocPageColumn}>Page</Text>
      </View>

      <View style={PDFStyles.contentWithPadding}>
        {tocEntries.map((section, index) => {
          const pageNumber = sectionPages[section.id]
          return (
            <View key={section.id} style={PDFStyles.tocSectionRow}>
              <Text style={PDFStyles.tocSectionNumber}>
                {(index + 1).toFixed(1)}
              </Text>
              <Link
                src={`#${section.id}`}
                style={PDFStyles.tocSectionTitleLink}>
                <Text style={PDFStyles.tocSectionTitle}>{section.title}</Text>
              </Link>
              <View style={PDFStyles.tocDotContainer}>
                <View style={PDFStyles.tocDots} />
              </View>
              <Text style={PDFStyles.tocSectionPage}>
                {pageNumber?.toString() || '-'}
              </Text>
            </View>
          )
        })}
      </View>

      <PageFooter
        jobNumber={formData.coverPageData.geckoJobId}
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={
          !(
            formData.signoff?.reviewerSignoff &&
            formData.signoff.supervisorSignoff
          )
        }
      />
    </Page>
  )
}

export const TOC: TOCSection = {
  id: 'tableOfContents',
  title: 'Table of Contents',
  RenderPDF,
  RenderReact,
}
